import { string, object, number, mixed } from "yup";
import i18next from "/app/src/locales/config";
import { ReportColumn } from "/app/src/models/reportColumn";

// Constants for special filter values that should skip validation
const SPECIAL_FILTER_VALUES = {
  NO_VALUE: "no_value",
  BLANK: "Blank",
  COLUMN_PREFIX: "column:",
} as const;

// UUID validation regex pattern (extracted as constant for performance)
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validate UUID format
 */
function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Validate if a string is a valid number
 */
function isValidNumber(value: string): boolean {
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num);
}

/**
 * Check if a value is a special filter value that should skip validation
 */
function isSpecialFilterValue(value: string): boolean {
  return (
    value === SPECIAL_FILTER_VALUES.NO_VALUE ||
    value === SPECIAL_FILTER_VALUES.BLANK ||
    value.startsWith(SPECIAL_FILTER_VALUES.COLUMN_PREFIX)
  );
}

/**
 * Convert value to a string representation for validation
 * Arrays are joined with commas to match server-side format
 */
function valueToString(value: unknown): string {
  if (value === null) return "";
  return Array.isArray(value)
    ? value.map((v) => String(v)).join(",")
    : String(value);
}

/**
 * Validate filter value based on the report column's filter type
 */
function validateFilterValue(
  value: unknown,
  reportColumnId: number | undefined,
  reportColumns: ReportColumn[],
): string | undefined {
  // Skip validation if no column selected or no value provided
  // Also skip empty strings or whitespace-only strings
  if (!reportColumnId || !value) {
    return undefined;
  }
  if (typeof value === "string" && value.trim() === "") {
    return undefined;
  }

  // Find the report column
  const reportColumn = reportColumns.find((col) => col.id === reportColumnId);
  if (!reportColumn || !reportColumn.filterType) {
    return undefined;
  }

  // Skip validation for special values
  const valueStr = valueToString(value);
  if (isSpecialFilterValue(valueStr)) {
    return undefined;
  }

  // Get values as array for validation
  // If value is already an array, use it. Otherwise, split by comma for multi-value support
  let values: string[];
  if (Array.isArray(value)) {
    // Flatten any comma-separated values within array elements
    values = value.flatMap((v) =>
      String(v)
        .split(",")
        .map((s) => s.trim()),
    );
  } else {
    // Split comma-separated string into array
    values = String(value)
      .split(",")
      .map((s) => s.trim());
  }

  // Validate based on filter type
  if (reportColumn.filterType === "Number") {
    for (const val of values) {
      if (!isValidNumber(val)) {
        return i18next.t("translation:invalid_numeric_value", {
          value: val,
          defaultValue: `Invalid value '${val}' for numeric filter. Please enter a valid number.`,
        });
      }
    }
  } else if (reportColumn.filterType === "UUID") {
    for (const val of values) {
      if (!isValidUUID(val)) {
        return i18next.t("translation:invalid_uuid_value", {
          value: val,
          defaultValue: `Invalid value '${val}' for UUID filter. Please enter a valid UUID.`,
        });
      }
    }
  }

  return undefined;
}

export const reportExportSchema = object().shape({
  exportType: string().required(i18next.t("translation:required")),

  integrationId: string().when("exportType", {
    is: (exportType) => /CSV/.test(exportType), //use regex to test if exportType contains "CSV"
    then: (schema) =>
      schema.required(i18next.t("translation:required")).nullable(),
    otherwise: (schema) => schema.nullable(),
  }),
});

/**
 * Create a report filter schema with report columns context
 */
export const createReportFilterSchema = (reportColumns: ReportColumn[]) => {
  return object().shape({
    verb: string().required(i18next.t("translation:required")),
    reportColumnId: number().required(i18next.t("translation:required")),
    value: mixed()
      .transform((value) => {
        if (Array.isArray(value) && value.length === 0) {
          return null;
        }
        return value;
      })
      .required(i18next.t("translation:required"))
      .test("validate-filter-value", function (value) {
        const { reportColumnId } = this.parent;
        const error = validateFilterValue(value, reportColumnId, reportColumns);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    dateRange: string().when("verb", {
      is: (verb) => ["before", "after"].includes(verb),
      then: (schema) =>
        schema.nonNullable().required(i18next.t("translation:required")),
      otherwise: (schema) => schema.nullable(),
    }),
  });
};

// Keep the old schema for backward compatibility, but it won't have validation
export const reportFilterSchema = object().shape({
  verb: string().required(i18next.t("translation:required")),
  reportColumnId: number().required(i18next.t("translation:required")),
  value: mixed()
    .transform((value) => {
      if (Array.isArray(value) && value.length === 0) {
        return null;
      }
      return value;
    })
    .required(i18next.t("translation:required")),
  dateRange: string().when("verb", {
    is: (verb) => ["before", "after"].includes(verb),
    then: (schema) =>
      schema.nonNullable().required(i18next.t("translation:required")),
    otherwise: (schema) => schema.nullable(),
  }),
});

export const updateReportFilterSchema = object().shape({
  value: mixed()
    .transform((value) => {
      if (Array.isArray(value) && value.length === 0) {
        return null;
      }
      return value;
    })
    .required(i18next.t("translation:required")),
});
